import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, MapPin, Envelope, ArrowRight, WhatsappLogo } from "@phosphor-icons/react";
import { COMPANY, PAYLOAD_TYPES, waLink } from "@/data/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  pickup: "",
  drop: "",
  payload_type: "",
  message: "",
};

export default function QuoteContact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup || !form.drop) {
      toast.error("Please fill name, phone, pickup and drop.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.payload_type) delete payload.payload_type;
      if (!payload.message) delete payload.message;
      await axios.post(`${API}/inquiries`, payload);
      toast.success("Quote request received. We'll call you shortly.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Submission failed. Please try calling us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border-2 border-slate-900 px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-[#EA580C]/30 focus:border-[#EA580C] transition-brutal";

  return (
    <section
      id="quote"
      className="py-20 lg:py-32 bg-[#F8FAFC]"
      data-testid="quote-contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-[2px] bg-[#EA580C]" />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-slate-500">
              Get in touch
            </span>
          </div>
          <h2
            id="contact"
            className="font-display font-black text-slate-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]"
          >
            Request a quote.
            <br />
            <span className="text-slate-400">We'll reply within hours.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5 space-y-6"
          >
            <div className="p-8 bg-[#0F172A] text-white border-2 border-slate-900 shadow-brutal">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 mb-3">
                Headquarters
              </div>
              <div className="flex items-start gap-3">
                <MapPin
                  size={22}
                  weight="duotone"
                  className="text-[#F59E0B] mt-1 shrink-0"
                />
                <div>
                  <div className="font-display font-black text-2xl leading-tight">
                    Shree Ambey Roadlines
                  </div>
                  <div className="mt-2 text-white/80 text-sm leading-relaxed">
                    {COMPANY.address.line1}
                    <br />
                    {COMPANY.address.line2}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 bg-white border-2 border-slate-900 shadow-brutal">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-4">
                Call the Owners
              </div>
              <div className="space-y-4">
                {COMPANY.owners.map((o) => (
                  <div
                    key={o.name}
                    className="p-4 bg-[#F1F5F9] border-2 border-slate-900"
                    data-testid={`contact-owner-${o.name.split(" ")[0].toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-display font-bold text-lg leading-tight text-slate-900">
                          {o.name}
                        </div>
                        <div className="text-xs uppercase tracking-wider text-slate-500">
                          Owner
                        </div>
                      </div>
                      <div className="font-mono font-bold text-lg text-slate-900">
                        {o.phone}
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <a
                        href={`tel:${o.phone}`}
                        data-testid={`contact-call-${o.name.split(" ")[0].toLowerCase()}`}
                        className="inline-flex items-center justify-center gap-2 bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider px-3 py-3 border-2 border-slate-900 transition-brutal hover:bg-[#C2410C]"
                      >
                        <Phone size={14} weight="bold" />
                        Call
                      </a>
                      <a
                        href={waLink(o)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`contact-wa-${o.name.split(" ")[0].toLowerCase()}`}
                        className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white text-xs font-bold uppercase tracking-wider px-3 py-3 border-2 border-slate-900 transition-brutal hover:bg-[#128C7E]"
                      >
                        <WhatsappLogo size={14} weight="bold" />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-2 border-dashed border-slate-400 bg-white">
              <div className="flex items-center gap-3 text-slate-700">
                <Envelope size={20} weight="duotone" className="text-[#EA580C]" />
                <span className="text-sm font-medium">
                  Prefer email? Share details below and we'll get back.
                </span>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            onSubmit={submit}
            noValidate
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-7 p-8 lg:p-10 bg-white border-2 border-slate-900 shadow-brutal"
            data-testid="quote-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
                  Your Name *
                </label>
                <input
                  data-testid="quote-input-name"
                  value={form.name}
                  onChange={update("name")}
                  className={inputCls}
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
                  Phone *
                </label>
                <input
                  data-testid="quote-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputCls}
                  placeholder="+91 98xxxxxxxx"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
                  Email (optional)
                </label>
                <input
                  data-testid="quote-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className={inputCls}
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
                  Pickup Location *
                </label>
                <input
                  data-testid="quote-input-pickup"
                  value={form.pickup}
                  onChange={update("pickup")}
                  className={inputCls}
                  placeholder="City / Warehouse"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
                  Drop Location *
                </label>
                <input
                  data-testid="quote-input-drop"
                  value={form.drop}
                  onChange={update("drop")}
                  className={inputCls}
                  placeholder="City / State"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
                  Payload Type
                </label>
                <select
                  data-testid="quote-input-payload"
                  value={form.payload_type}
                  onChange={update("payload_type")}
                  className={inputCls}
                >
                  <option value="">Select payload category</option>
                  {PAYLOAD_TYPES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">
                  Message / Requirements
                </label>
                <textarea
                  data-testid="quote-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="Approximate weight, timeline, any special handling…"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="mt-8 inline-flex items-center gap-3 bg-[#EA580C] text-white font-bold uppercase tracking-wider text-sm px-8 py-4 border-2 border-slate-900 shadow-brutal hover-shadow-brutal transition-brutal disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Sending…" : "Send Quote Request"}
              <ArrowRight size={18} weight="bold" />
            </button>
            <p className="mt-4 text-xs text-slate-500 font-medium">
              By submitting you agree to be contacted about your shipment.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
